import { palette } from '@/brand-utgl/theme';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { HyperlinkButton } from '@/design-system/components/buttons/hyperlinkButton/HyperlinkButton';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { getObjectScreenNameFromPath, popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { H5, H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ScrollView, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, useWindowDimensions } from 'react-native';
import { RootStackRoutes } from 'types';
import BillPaymentApp from '../..';
import { UseBillPaymentFormProp } from '../../hooks/useBillPaymentForm.hook';
import { PAYMENT_TYPE, QuoteResponse, UseCreateBillPaymentProp } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { mapUrlLink, onGoBack } from '../../utils/utils';
import { HelpCenterComponent } from '../components/HelpCenter.component';
import { ScheduleComponentWebComponent } from '../components/scheduleComponent/ScheduleComponentWeb.component';
import { DetailFormWebComponent } from './DetailFormWeb.component';

export interface BillPaymentFormWebProps {
  useBillPaymentForm: UseBillPaymentFormProp;
  useCreateBillPayment: UseCreateBillPaymentProp;
  imageComponent: JSX.Element;
}

export const BillPaymentFormWebComponent: React.FC<BillPaymentFormWebProps> = ({
  useBillPaymentForm,
  useCreateBillPayment,
  imageComponent
}) => {
  const { t } = useTranslation('global-bill-payment');
  const [submittedButton, setSubmittedButton] = useState(false);
  const navigation = useNavigation();
  const {
    detailForm: { onValidateVerification },
    scheduleForm: { onValidateScheduleForm },
    general: {
      validated,
      quoteDataRequest,
      debitAmount,
      billPaymentFee,
      isLoadingFee,
      setValidated,
      setFlagSubmitted,
      documentSelected,
      quoteResponseProps
    }
  } = useBillPaymentForm;
  const {
    general: { alertMessage, setAlertMessage, mapSearchWithResponse, paymentType },
    instruction: { fillFormValuesToQuotedRequest, postQuote, mapQuoteDataRequest, isLoadingQuote },
    postCreateBillPayment: {}
  } = useCreateBillPayment;
  const { width } = useWindowDimensions();
  const widthRightLayout = (width / 154) * 44;

  useEffect(() => {
    if (submittedButton && validated) {
      setSubmittedButton(false);
      setValidated(false);
      setAlertMessage('');

      const request = fillFormValuesToQuotedRequest(useBillPaymentForm, quoteDataRequest);
      postQuote(
        request,
        (data) => {
          const response: QuoteResponse = data.data;
          const debitAccount = `${useBillPaymentForm.detailForm.debitAccountWalletName}\n${useBillPaymentForm.detailForm.debitAccountWallerId}`;
          navigation.navigate(RootStackRoutes.BillPayment, {
            screen: BillPaymentStackRoutes.Confirmation,
            params: {
              request: fillFormValuesToQuotedRequest(useBillPaymentForm, mapQuoteDataRequest(quoteDataRequest, response)),
              response: mapSearchWithResponse(response, quoteResponseProps!),
              flagUploaded: true,
              debitAccount: debitAccount,
              documentSelected: documentSelected,
              paymentType: paymentType!
            }
          });
        },
        (error) => {
          console.log(error);
          const { data } = error.response || ({} as any);
          const errorMessage = data && _.isString(data.message) ? data.message : error.message;
          if (!onValidateVerification(errorMessage)) setAlertMessage(errorMessage);
        }
      );
    }
    const timeout = setTimeout(() => setSubmittedButton(false), 500);
    return () => clearTimeout(timeout);
  }, [submittedButton, validated]);

  const onBack = () => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      onGoBack(navigation);
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      onGoBack(navigation);
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
          setTimeout(() => {
            const popStateLocked = popStateLockedMap.get('popstate') ?? false;
            if (!document.location.href || popStateLocked) return;
            const { parentName, subName } = mapUrlLink(document.location.href);
            var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
            if (
              screenNameObject.screenName &&
              screenNameObject.routeStackName === parentName &&
              (screenNameObject.screenName === BillPaymentStackRoutes.Form ||
                screenNameObject.screenName === BillPaymentStackRoutes.Confirmation)
            ) {
              onBack();
            }
          }, 200);
        };
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  return (
    <View sx={sxStyles.container}>
      <ScrollView sx={sxStyles.scrollView} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.toolbar}>
            <HyperlinkButton
              icon="arrowLeft"
              iconPosition="left"
              iconSize={20}
              label={t('global-bill-payment::glossary.back-to-payment-overview')}
              onPress={() => onBack()}
            />
          </View>

          <View sx={sxStyles.viewRow}>
            <View sx={sxStyles.spaceX} />
            <H5 bold sx={sxStyles.title}>
              {paymentType === PAYMENT_TYPE.GLOBAL
                ? t('global-bill-payment::glossary.title--overseas-bill-payment')
                : t('global-bill-payment::glossary.title--local-bill-payment')}
            </H5>
          </View>
          <View sx={sxStyles.content}>
            <View sx={sxStyles.spaceX} />
            <View sx={sxStyles.leftComponent}>
              <DetailFormWebComponent sx={{ zIndex: 4 }} billPaymentProp={useBillPaymentForm} />
              <ScheduleComponentWebComponent sx={{ ...sxStyles.viewMargin10, ...{ zIndex: 3 } }} billPaymentProp={useBillPaymentForm} />
            </View>
            <View sx={sxStyles.space} />
            <View sx={sxStyles.rightComponent}>
              <View sx={{ width: widthRightLayout, flexDirection: 'column' }}>
                {imageComponent}

                <View sx={{ ...sxStyles.viewColumn, ...sxStyles.viewMargin10 }}>
                  <TextTitle semiBold={true}>{t('global-bill-payment::instruction.text--notice-make-sure')}</TextTitle>
                  <View sx={{ ...sxStyles.viewRow, ...sxStyles.viewMargin12 }}>
                    <UniButton
                      variant={ButtonVariant.outline1}
                      sx={sxStyles.viewButton}
                      label={t('global-bill-payment::instruction.button--injection')}
                      onPress={() => iframePostMessage('showInjectionInstruction', true)}
                      size="medium"
                    />
                    <View sx={{ mr: '$3' }} />
                    <UniButton
                      variant={ButtonVariant.outline1}
                      sx={sxStyles.viewButton}
                      label={t('global-bill-payment::instruction.button--exchange')}
                      onPress={() => iframePostMessage('moveToExchange', true)}
                      size="medium"
                    />
                  </View>
                </View>

                <HelpCenterComponent sx={sxStyles.viewMargin10} />
              </View>
            </View>
            <View sx={sxStyles.spaceX} />
          </View>
          <View sx={sxStyles.viewRow}>
            <View sx={sxStyles.spaceX} />
            <View sx={{ ...sxStyles.leftComponent, ...sxStyles.viewRow }}>
              <View sx={sxStyles.footerLeft} />
              <View sx={sxStyles.dividerFooter} />
              <View sx={{ flex: 52 }}>
                {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={sxStyles.viewAlert} />}
              </View>
            </View>
            <View sx={sxStyles.space} />
            <View sx={sxStyles.rightComponent} />
            <View sx={sxStyles.spaceX} />
          </View>
          <View sx={sxStyles.footerLayout}>
            <View sx={sxStyles.spaceX} />
            <View sx={{ ...sxStyles.leftComponent, ...sxStyles.viewRow }}>
              <View sx={sxStyles.footerLeft} />
              <View sx={sxStyles.dividerFooter} />
              <View sx={sxStyles.buttonLayout}>
                <View sx={sxStyles.viewDebitAmountWrapper}>
                  <TextCaption semiBold={true}>{t('global-bill-payment::glossary.debit-amount')}</TextCaption>
                  <H6 sx={{ color: palette.Red.$600, mt: '$1' }} bold={true}>
                    {debitAmount}
                  </H6>
                  <TextBody sx={sxStyles.marginTop4}>
                    {t('global-bill-payment::payment-details.glossary.text--instruction-fee-included').replace(
                      '{{amount}}',
                      billPaymentFee
                    )}
                  </TextBody>
                </View>
                <View>
                  <UniButton
                    label={t('global-bill-payment::glossary.submit')}
                    onPress={() => {
                      setFlagSubmitted(true);
                      setSubmittedButton(true);
                      onValidateScheduleForm();
                    }}
                    sx={sxStyles.button}
                    size="medium"
                    loading={isLoadingQuote}
                    disabled={isLoadingFee || isLoadingQuote}
                  />
                  <UniButton
                    sx={{ ...sxStyles.button, ...sxStyles.viewBackMargin }}
                    labelSx={sxStyles.backLabel}
                    size="large"
                    variant={ButtonVariant.outline1}
                    onPress={onBack}
                    label={t('global-bill-payment::glossary.back')}
                  />
                </View>
              </View>
            </View>
            <View sx={sxStyles.space} />
            <View sx={sxStyles.rightComponent} />
            <View sx={sxStyles.spaceX} />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    flex: 1
  },
  body: {
    backgroundColor: '$backgroundPrimary',
    flex: 1,
    borderRadius: 15,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10,
    overflow: 'hidden'
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$7',
    backgroundColor: '$backgroundSecondary'
  },
  textHeader: {
    color: '$primary',
    ml: '$2'
  },
  title: {
    flex: 142,
    pt: '$10',
    pb: '$5'
  },
  content: {
    pt: '$5',
    pb: '$19',
    flexDirection: 'row',
    zIndex: 2
  },
  leftComponent: {
    flex: 78,
    flexDirection: 'column',
    zIndex: 999
  },
  space: {
    flex: 8,
    maxWidth: '$20'
  },
  spaceX: {
    flex: 12,
    zIndex: 1
  },
  rightComponent: {
    flex: 44,
    flexDirection: 'column',
    zIndex: 1
  },
  footerLayout: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#DCDCDC',
    zIndex: 1,
    mt: '$8'
  },
  buttonLayout: {
    flex: 52,
    flexDirection: 'row',
    justifyContent: 'space-between',
    py: '$5',
    pl: '$8',
    minWidth: 450
  },
  button: {
    width: 180,
    height: '$11'
  },
  viewMargin12: {
    mt: '$3'
  },
  viewMargin10: {
    mt: '$10'
  },
  backLabel: {
    color: 'black'
  },
  viewRow: {
    flexDirection: 'row'
  },
  viewColumn: {
    flexDirection: 'column'
  },
  viewDebitAmountWrapper: {
    flex: 1
  },
  viewBackMargin: {
    mt: '$3'
  },
  scrollView: {
    padding: '$3'
  },
  footerLeft: {
    flex: 29
  },
  marginTop4: {
    mt: '$1'
  },
  dividerFooter: {
    flex: 4
  },
  flex8: {
    flex: 8
  },
  flex92: {
    flex: 92
  },
  flex208: {
    flex: 208
  },
  viewButton: {
    px: '$5'
  },
  viewAlert: {
    mt: '$5'
  }
};
